import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface Participant {
  id: string;
  name: string;
}

interface ParticipantsContextType {
  participants: Participant[];
  setParticipants: React.Dispatch<React.SetStateAction<Participant[]>>;
  removeParticipant: (id: string) => void;
  resetParticipants: () => void;
  saveAsDefault: () => void;
  clearParticipants: () => void;
}

const defaultParticipants: Participant[] = [
  { id: '1', name: 'Alice' },
  { id: '2', name: 'Bob' },
  { id: '3', name: 'Charlie' },
  { id: '4', name: 'Diana' },
  { id: '5', name: 'Eve' },
  { id: '6', name: 'Frank' }
];

export { defaultParticipants };

// Try to load saved participants from localStorage with improved error handling
const getSavedParticipants = (): Participant[] | null => {
  try {
    const saved = localStorage.getItem('savedParticipants');
    if (!saved) return null;
    
    const parsed = JSON.parse(saved);
    // Validate that parsed data is an array and has the expected structure
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    
    // Verify each participant has id and name properties
    for (const p of parsed) {
      if (!p.id || !p.name) return null;
    }
    
    return parsed;
  } catch (error) {
    console.error('Error loading saved participants:', error);
    // If any error occurs, clear the corrupted localStorage entry
    try {
      localStorage.removeItem('savedParticipants');
    } catch (e) {
      // Ignore any errors on cleanup
    }
    return null;
  }
};

const ParticipantsContext = createContext<ParticipantsContextType | undefined>(undefined);

export const ParticipantsProvider = ({ children }: { children: React.ReactNode }) => {
  // Initialize with saved participants or default participants
  const [participants, setParticipants] = useState<Participant[]>(() => {
    const saved = getSavedParticipants();
    return saved || defaultParticipants;
  });

  const removeParticipant = (id: string) => {
    setParticipants(prev => prev.filter(p => p.id !== id));
  };

  const resetParticipants = useCallback(() => {
    // Always reset to default participants
    setParticipants(defaultParticipants);
  }, [setParticipants]);

  const saveAsDefault = useCallback(() => {
    // Use a function to get the latest state
    setParticipants(currentParticipants => {
      if (currentParticipants.length > 0) {
      try {
          localStorage.setItem('savedParticipants', JSON.stringify(currentParticipants));
      } catch (error) {
        console.error('Error saving participants:', error);
      }
    } else {
      try {
        localStorage.removeItem('savedParticipants');
      } catch (error) {
        // Ignore
      }
    }
      return currentParticipants;
    });
  }, []);

  const clearParticipants = () => {
    setParticipants([]);
  };

  return (
    <ParticipantsContext.Provider value={{ 
      participants, 
      setParticipants, 
      removeParticipant, 
      resetParticipants,
      saveAsDefault,
      clearParticipants
    }}>
      {children}
    </ParticipantsContext.Provider>
  );
};

export const useParticipants = () => {
  const context = useContext(ParticipantsContext);
  if (!context) throw new Error('useParticipants must be used within a ParticipantsProvider');
  return context;
}; 